import {
  Check,
  DoDisturb,
  PendingActions,
  QuestionMark,
} from '@mui/icons-material';
import { Chip } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { JSX, useEffect, useState } from 'react';

type AthenaStatusChipProps = {
  workflowState: string | null | undefined;
};

/**
 * AthenaStatusChip: Unified status display component for all workflow states
 * Displays workflow states with appropriate colors, icons, and text styling
 */
const AthenaStatusChip = (props: AthenaStatusChipProps) => {
  const [chipColor, setChipColor] = useState<string | undefined>('#1F4D83');
  const [chipIcon, setChipIcon] = useState<JSX.Element>(<QuestionMark />);
  const [textColor, setTextColor] = useState<'white' | 'black'>('white');

  useEffect(() => {
    switch (props.workflowState) {
      case 'Internally Approved':
      case 'Federally Approved':
      case 'No Workflow, Approved': {
        setChipColor('#6BA539');
        setChipIcon(<Check />);
        setTextColor('white');
        break;
      }
      // Internal-blue (Tyto). Site Lead and PM are Tyto roles — grouped with the
      // legacy internal states to match Phase.Tier == TierInternal in the
      // backend workflow registry (workflow_phases.go).
      case 'In Internal Review':
      case 'Pending':
      case 'Waiting on Tyto Approval':
      case 'In Site Lead Review':
      case 'In PM Review': {
        setChipColor(alpha('#0092C8', 0.8));
        setChipIcon(<PendingActions />);
        setTextColor('white');
        break;
      }
      // Federal-purple (Government). APM, DPdM and COR are Government roles —
      // grouped with the legacy federal states to match Phase.Tier ==
      // TierFederal in the backend workflow registry (workflow_phases.go).
      case 'In Federal Review':
      case 'Waiting on Federal Approval':
      case 'In APM Review':
      case 'In DPdM Review':
      case 'In Theater Director Review':
      case 'In COR Review': {
        setChipColor(alpha('#7B1E8E', 0.8));
        setChipIcon(<PendingActions />);
        setTextColor('white');
        break;
      }
      case 'Internally Rejected':
      case 'Federally Rejected':
      case 'Internally Rejected, In Recovery':
      case 'Federally Rejected, In Recovery': {
        setChipColor('#C51E46');
        setChipIcon(<DoDisturb />);
        setTextColor('white');
        break;
      }
      default: {
        setChipColor('#1F4D83');
        setChipIcon(<QuestionMark />);
        setTextColor('white');
        break;
      }
    }
  }, [props.workflowState]);

  return (
    <Chip
      sx={{
        backgroundColor: chipColor,
        borderRadius: '15px',
        justifyContent: 'center',
        color: textColor,
        width: 200,
        '& .MuiChip-icon': {
          color: textColor,
        },
        '& .MuiChip-label': {
          flex: 1,
          textAlign: 'center',
        },
      }}
      icon={chipIcon}
      label={props.workflowState ?? 'Unknown'}
      size="medium"
    />
  );
};

export default AthenaStatusChip;
